import { useQueries, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  getTplHistory,
  getTplProviders,
  PfcError,
  type TplHistory,
  type TplProvider,
} from "@/modules/fulfilment_costs/api";
import { COMPANY_LABEL } from "@/modules/fulfilment_costs/TplHistoryTab";

import type { PageProps } from "../index";
import { CHART, CRITERIA_COLOURS, TOOLTIP_STYLE } from "../charts";
import { Footnote, money, Note, PageHead } from "../kit";
import {
  actualMonthly,
  actualPerParcel,
  agedStockSaving,
  belowIndexFloor,
  BLOCKS,
  chosenIds,
  companyOptions,
  CURRENT_3PLS,
  decision,
  defaultDrivers,
  defaultFrom,
  defaultTo,
  fetchFrom,
  INDEX_METRICS,
  INDEX_FLOOR,
  indexGaps,
  indexRows,
  lastBilled,
  lastCompleteMonth,
  moveTfiSaving,
  parseList,
  PARTS,
  parcelsPerMonth,
  perParcelByPart,
  rankStandardised,
  regionOptions,
  rates as activityRates,
  runRate,
  selectableProviders,
  shortMonth,
  standardised,
  trendRows,
  validDrivers,
  validFrom,
  validTo,
  yearRows,
  ym,
  type Drivers,
  type IndexMetric,
  type PerParcel,
  type PerParcelKey,
  type ProviderId,
  type RunRate,
  type Standardised,
} from "./tplCostModel";
import { MultiPick } from "./MultiPick";

const QUERY_ROOT = ["sourcing", "tpl_cost_model"] as const;
const LINE_COLOURS = [CHART.purple, CHART.purple2, CHART.ink, CHART.warn] as const;
const ROW_ERROR = "We can't reach the fulfilment cost data right now. Try again shortly.";

function lineColour(id: string, chosen: string[]): string {
  const currentIndex = (CURRENT_3PLS as readonly string[]).indexOf(id);
  if (currentIndex >= 0) return LINE_COLOURS[currentIndex];
  const otherIndex = chosen.filter((chosenId) => !(CURRENT_3PLS as readonly string[]).includes(chosenId)).indexOf(id);
  return CRITERIA_COLOURS[(4 + otherIndex) % CRITERIA_COLOURS.length];
}

const finiteMoney = (value: number | null | undefined, dp = 2): string =>
  value != null && Number.isFinite(value) ? money(value, dp) : "—";
export function moneyIn(value: number | null | undefined, currency: string, dp = 2): string {
  if (currency === "GBP") return finiteMoney(value, dp);
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
  }
}
const percent = (value: number | null, suspect = false): string =>
  value == null || !Number.isFinite(value) ? "—" : `${value >= 0 ? "+" : ""}${Math.round(value * 100)}%${suspect ? "*" : ""}`;
const inputNumber = (raw: unknown): number | null => {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};
const LONG_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const longMonth = (month: string): string => `${LONG_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
const errorStatus = (error: unknown): number | null => error instanceof PfcError ? error.status : null;

function Failure({ status }: { status: number | null }) {
  const copy = status === 404
    ? "3PL bills are not available on the Fulfilment Costs engine yet."
    : status === 403
      ? "This page reads Precision Fulfilment's 3PL bills. Ask the Margin Engine area owner for access."
      : "We can't reach the fulfilment cost data right now. Try again shortly.";
  return <Alert><AlertDescription>{copy}</AlertDescription></Alert>;
}

function Pending() {
  return (
    <div className="grid gap-8">
      <ChartFrame title="Actual yearly cost per 3PL, by part" titleVisible pending><BarChart /></ChartFrame>
      <ChartFrame title="Actual monthly cost per 3PL" titleVisible pending><LineChart /></ChartFrame>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-base font-semibold text-ks-ink">{children}</h2>;
}

function providerMap(providers: TplProvider[]): Map<string, TplProvider> {
  return new Map(providers.map((provider) => [provider.provider_id, provider]));
}

function yearlyValue(rate: RunRate, parts: readonly string[]): number | null {
  if (!rate.yearlyParts) return null;
  return parts.reduce((sum, part) => sum + rate.yearlyParts![part as keyof typeof rate.yearlyParts], 0);
}

function EmptyProviderCells({ copy, columns, error = false }: { copy: string; columns: number; error?: boolean }) {
  return <TableCell colSpan={columns} className="text-muted-foreground">{error ? <span role="alert">{copy}</span> : copy}</TableCell>;
}

function emptyRowCopy(id: string, provider: TplProvider | undefined, history: TplHistory | undefined, rate: RunRate | undefined, loading: Set<string>, errors: Set<string>, fetchTo: string): string | null {
  if (!provider) return "Not in the engine yet";
  if (loading.has(id)) return "Loading…";
  if (errors.has(id)) return ROW_ERROR;
  if (!history || !rate) return "No bills in this range";
  if (rate.monthsBilled > 0) return null;
  const last = lastBilled(history, fetchTo);
  return last ? `Last billed ${shortMonth(last)}: move From back to include its months` : "No bills in this range";
}

type RankedStandardised = Standardised & { vsCheapest: number | null };

function AllProvidersToday({
  ids,
  providers,
  rates,
}: {
  ids: string[];
  providers: Map<string, TplProvider>;
  rates: Map<string, RunRate>;
}) {
  const rows = ids.map((id) => {
    const provider = providers.get(id);
    const rate = rates.get(id);
    const monthly = rate ? actualMonthly(rate) : null;
    const lowVolume = monthly != null && monthly.parcels < INDEX_FLOOR;
    return { id, provider, rate, monthly, lowVolume };
  });
  const chartRows = rows.map(({ id, provider, rate, monthly, lowVolume }) => ({
    label: `${provider?.label ?? id}${lowVolume ? " · low volume" : ""}`,
    ...Object.fromEntries(PARTS.map((part) => [part, rate && rate.monthsBilled > 0 ? rate.parts[part] / rate.monthsBilled : null])) as Record<(typeof PARTS)[number], number | null>,
    total: monthly?.billed ?? null,
  }));

  return <section className="grid gap-3">
    <SectionTitle>All 3PLs today</SectionTitle>
    <Table>
      <TableHeader><TableRow>
        <TableHead>3PL</TableHead>
        <TableHead>Region</TableHead>
        <TableHead className="text-right">Months billed</TableHead>
        <TableHead className="text-right">Billed a month</TableHead>
        <TableHead className="text-right">Parcels a month</TableHead>
        <TableHead className="text-right">Cost per parcel</TableHead>
        <TableHead className="text-right">Storage a month</TableHead>
        <TableHead className="text-right">Subscription a month</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map(({ id, provider, rate, monthly, lowVolume }) => <TableRow key={`today-${id}`}>
        <TableCell className="font-medium">{provider?.label ?? id}{lowVolume ? <span className="ml-2 text-xs font-normal text-muted-foreground">low volume</span> : null}</TableCell>
        <TableCell>{provider?.region ?? "—"}</TableCell>
        <TableCell className="text-right">{rate?.monthsBilled ?? 0}</TableCell>
        <TableCell className="text-right">{moneyIn(monthly?.billed, "GBP", 0)}</TableCell>
        <TableCell className="text-right">{monthly ? monthly.parcels.toLocaleString("en-GB", { maximumFractionDigits: 0 }) : "—"}</TableCell>
        <TableCell className="text-right">{monthly?.perParcel == null ? "No parcels" : moneyIn(monthly.perParcel, "GBP", 2)}</TableCell>
        <TableCell className="text-right">{moneyIn(monthly?.storage, "GBP", 0)}</TableCell>
        <TableCell className="text-right">{moneyIn(monthly?.subscription, "GBP", 0)}</TableCell>
      </TableRow>)}</TableBody>
    </Table>
    <ChartFrame title="Actual cost a month per 3PL, by part" titleVisible pointCount={chartRows.length}>
      <BarChart data={chartRows} layout="vertical" margin={{ left: 12, right: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={(value) => finiteMoney(Number(value), 0)} />
        <YAxis type="category" dataKey="label" width={128} />
        <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), 0)} />
        <Legend />
        {PARTS.map((part, index) => <Bar key={part} dataKey={part} name={part} stackId="a" fill={CRITERIA_COLOURS[index]}>{index === PARTS.length - 1 ? <LabelList dataKey="total" position="right" formatter={(value: unknown) => `${finiteMoney(Number(value), 0)} a month`} /> : null}</Bar>)}
      </BarChart>
    </ChartFrame>
  </section>;
}

function StandardisedCostTable({
  ids, providers, histories, rates, loading, errors, fetchTo, from, to, drivers, driversTyped, d2cRaw, fbaRaw, rows, setSearch, invoiceMode,
}: {
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  rates: Map<string, RunRate>;
  loading: Set<string>;
  errors: Set<string>;
  fetchTo: string;
  from: string;
  to: string;
  drivers: Drivers | null;
  driversTyped: boolean;
  d2cRaw: string;
  fbaRaw: string;
  rows: RankedStandardised[];
  setSearch: (key: string, value: string) => void;
  invoiceMode: boolean;
}) {
  const completeRows = rows.filter((row) => row.complete && row.total != null);
  const byId = new Map(rows.map((row) => [row.providerId, row]));
  const incompleteRows = rows.filter((row) => !row.complete && (rates.get(row.providerId)?.monthsBilled ?? 0) > 0);
  const orderedIds = drivers
    ? [...rows.map((row) => row.providerId), ...ids.filter((id) => !rows.some((row) => row.providerId === id))]
    : ids;
  const chartRows = drivers ? completeRows.flatMap((row) => {
    const provider = providers.get(row.providerId);
    return provider ? [{
      label: provider.label,
      Fixed: row.blocks.Fixed / 12,
      "Customer parcels": row.blocks["Customer parcels"] / 12,
      "Amazon inbound": row.blocks["Amazon inbound"] / 12,
      "Other variable": row.blocks["Other variable"] / 12,
      total: row.total! / 12,
    }] : [];
  }) : [];
  const splitUnreliable = (id: string): boolean => {
    const history = histories.get(id);
    return history ? perParcelByPart(history, from, to).d2c === "unreliable" : false;
  };
  const combinedIds = orderedIds.filter((id) => rates.has(id) && splitUnreliable(id));
  const unrated = completeRows.flatMap((row) => {
    const rate = rates.get(row.providerId);
    return rate?.unratedMonths.length ? [{ id: row.providerId, months: rate.unratedMonths }] : [];
  });
  const monthlyDecision = decision(rows);
  const trailingColumns = drivers ? 10 : 6;
  const labelOf = (id: string): string => providers.get(id)?.label ?? id;
  const incompleteVolumes = incompleteRows.map((row) => parcelsPerMonth(rates.get(row.providerId)!)?.toLocaleString("en-GB", { maximumFractionDigits: 0 }) ?? "0");
  const incompleteNote = incompleteRows.length === 1
    ? `${labelOf(incompleteRows[0].providerId)} is not in this comparison: ${incompleteVolumes[0]} parcels a month is too few to read a price from its bills, so a rate card is needed. Its bills are under Actuals.`
    : `${incompleteRows.map((row) => labelOf(row.providerId)).join(" and ")} are not in this comparison: ${incompleteVolumes.join(" and ")} parcels a month are too few to read a price from their bills, so a rate card is needed. Their bills are under Actuals.`;
  const incompleteChartSubtitle = incompleteRows.length
    ? `${incompleteRows.map((row) => labelOf(row.providerId)).join(" and ")}: no price from their bills, shown in All 3PLs today.`
    : undefined;

  return <section className="grid gap-3">
    <SectionTitle>{`Standardised cost${invoiceMode ? " (GBP)" : ""}`}</SectionTitle>
    {monthlyDecision && drivers ? <Card>
      <CardHeader className="text-left">
        <CardTitle className="text-2xl font-bold">{monthlyDecision.aboutSame
          ? `${labelOf(monthlyDecision.cheapestId)} and ${labelOf(monthlyDecision.dearestId)} cost about the same (${moneyIn(monthlyDecision.gapPerMonth, "GBP", 0)} a month apart)`
          : `${labelOf(monthlyDecision.cheapestId)} costs ${moneyIn(monthlyDecision.gapPerMonth, "GBP", 0)} a month less than ${labelOf(monthlyDecision.dearestId)}`}</CardTitle>
        <CardDescription>Same work for both: {Math.round(drivers.d2c / 12).toLocaleString("en-GB")} customer parcels and {Math.round(drivers.fba / 12).toLocaleString("en-GB")} Amazon inbound parcels a month {driversTyped ? "(your volumes)." : `(J&J UK's average, ${longMonth(from)} to ${longMonth(to)}).`}{providers.get(monthlyDecision.cheapestId)?.region !== providers.get(monthlyDecision.dearestId)?.region
          ? ` ${labelOf(monthlyDecision.cheapestId)} (${providers.get(monthlyDecision.cheapestId)?.region}) and ${labelOf(monthlyDecision.dearestId)} (${providers.get(monthlyDecision.dearestId)?.region}) serve different markets, so this is a price benchmark at the same work, not a move of orders.`
          : ""}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <Table>
          <TableHeader><TableRow>
            <TableHead>3PL</TableHead>
            <TableHead className="text-right">Per-parcel work a month</TableHead>
            <TableHead className="text-right">Storage and subscription a month</TableHead>
            <TableHead className="text-right">Total a month</TableHead>
          </TableRow></TableHeader>
          <TableBody>{monthlyDecision.rows.map((row) => <TableRow key={`decision-${row.providerId}`}>
            <TableCell className="font-medium">{labelOf(row.providerId)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.workPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right font-semibold">{moneyIn(row.totalPerMonth, "GBP", 0)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
        <p className="text-sm">{monthlyDecision.fixedShareOfGap != null && monthlyDecision.fixedShareOfGap >= 0.5
          ? `${Math.round(monthlyDecision.fixedShareOfGap * 100)}% of the gap is storage and subscription. That follows how much stock each 3PL holds, which is not standardised yet, so the saving holds only if ${labelOf(monthlyDecision.cheapestId)} would store the same stock for less.`
          : `${Math.round((1 - (monthlyDecision.fixedShareOfGap ?? 0)) * 100)}% of the gap is per-parcel work, which is costed at the same volume for both.`}</p>
      </CardContent>
    </Card> : <Note>Pick at least two 3PLs that can be costed to see the saving a month.</Note>}
    <AllProvidersToday ids={ids} providers={providers} rates={rates} />
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold text-ks-ink">What this number leaves out</h3>
      <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        <li>Storage and subscription are each 3PL's own bill, not costed at the same stock.</li>
        <li>Monthly minimum charges are not applied.</li>
        {unrated.map(({ id, months }) => <li key={`unrated-${id}`}>{labelOf(id)}: {months.join(", ")} left out of its per-parcel prices (bills with no parcel data).</li>)}
      </ul>
    </div>
    <div className="grid gap-1">
      <h3 className="text-sm font-semibold text-ks-ink">Volumes every 3PL is costed at</h3>
      <p className="text-sm text-muted-foreground">J&amp;J UK's parcels from {longMonth(from)} to {longMonth(to)}, turned into a yearly figure: total ÷ {rates.get("jj_uk")?.monthsBilled ?? 0} months billed × 12. Change them to cost every 3PL at another volume.</p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="grid gap-1 text-sm">Customer parcels per year
        <Input data-nonmutating type="number" min={0} value={d2cRaw} onChange={(event) => setSearch("tc_d2c", event.target.value)} />
        {drivers ? <span className="text-muted-foreground">About {Math.round(drivers.d2c / 12).toLocaleString("en-GB")} a month.</span> : null}
      </label>
      <label className="grid gap-1 text-sm">Amazon inbound parcels per year
        <Input data-nonmutating type="number" min={0} value={fbaRaw} onChange={(event) => setSearch("tc_fba", event.target.value)} />
        {drivers ? <span className="text-muted-foreground">About {Math.round(drivers.fba / 12).toLocaleString("en-GB")} a month.</span> : null}
      </label>
    </div>
    <p className="text-sm text-muted-foreground">Storage, goods-in and reshipments: not standardised yet. The bills carry amounts, not volumes, so storage and subscription are taken as each 3PL's own fixed cost per year (Bojan, 2 Nov).</p>
    {!drivers ? <p className="text-sm text-muted-foreground">Type the yearly volumes to cost every 3PL at the same volume.</p> : null}
    <Table>
      <TableHeader><TableRow>
        <TableHead>3PL</TableHead>
        <TableHead className="text-right">Storage and subscription a month</TableHead>
        <TableHead className="text-right">Customer parcels a month</TableHead>
        <TableHead className="text-right">Amazon inbound a month</TableHead>
        <TableHead className="text-right">Other a month</TableHead>
        {drivers ? <>
          <TableHead className="text-right">Total a month</TableHead>
          <TableHead className="text-right">Total a year</TableHead>
          <TableHead className="text-right">vs cheapest</TableHead>
          <TableHead className="text-right">Cost per parcel at this volume</TableHead>
        </> : null}
        <TableHead className="text-right">Rate per customer parcel</TableHead>
        <TableHead className="text-right">Rate per Amazon inbound parcel</TableHead>
      </TableRow></TableHeader>
      <TableBody>{orderedIds.map((id) => {
        const provider = providers.get(id);
        const history = histories.get(id);
        const rate = rates.get(id);
        const row = byId.get(id);
        const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
        const providerRates = rate ? activityRates(rate, splitUnreliable(id)) : null;
        return <TableRow key={id}>
          <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
          {empty ? <EmptyProviderCells copy={empty} columns={trailingColumns} error={errors.has(id)} /> : row && !row.complete ? <>
            <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
            <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
            <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
            {drivers ? <>
              <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
              <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
              <TableCell className="text-right">—</TableCell>
              <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
            </> : null}
            <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
            <TableCell className="text-right">No price from its bills (rate card needed)</TableCell>
          </> : <>
            <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Customer parcels"] / 12 : null, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Amazon inbound"] / 12 : null, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Other variable"] / 12 : null, "GBP", 0)}</TableCell>
            {drivers ? <>
              <TableCell className="text-right font-semibold">{row?.total == null ? "—" : moneyIn(row.total / 12, "GBP", 0)}</TableCell>
              <TableCell className="text-right">{moneyIn(row?.total, "GBP", 0)}</TableCell>
              <TableCell className="text-right">{row?.vsCheapest == null ? "—" : `+${Math.round(row.vsCheapest * 100)}%`}</TableCell>
              <TableCell className="text-right">{moneyIn(row?.perParcel, "GBP", 2)}</TableCell>
            </> : null}
            <TableCell className="text-right">{providerRates?.combined ? "Not reliable (Amazon split)" : moneyIn(providerRates?.d2c, "GBP", 2)}</TableCell>
            <TableCell className="text-right">{providerRates?.combined ? "Not reliable (Amazon split)" : moneyIn(providerRates?.fba, "GBP", 2)}</TableCell>
          </>}
        </TableRow>;
      })}</TableBody>
    </Table>
    {incompleteRows.length ? <Note>{incompleteNote}</Note> : null}
    {drivers ? <ChartFrame title="Standardised cost a month by block" titleVisible subtitle={incompleteChartSubtitle} pointCount={chartRows.length}>
      <BarChart data={chartRows} layout="vertical" margin={{ left: 12, right: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={(value) => finiteMoney(Number(value), 0)} />
        <YAxis type="category" dataKey="label" width={96} />
        <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), 0)} />
        <Legend />
        {BLOCKS.map((block, index) => <Bar key={block} dataKey={block} name={block === "Fixed" ? "Storage and subscription" : block} stackId="a" fill={CRITERIA_COLOURS[index]}>{index === BLOCKS.length - 1 ? <LabelList dataKey="total" position="right" formatter={(value: unknown) => `${finiteMoney(Number(value), 0)} a month`} /> : null}</Bar>)}
      </BarChart>
    </ChartFrame> : null}
    {combinedIds.map((id) => <Footnote key={`combined-${id}`}>{providers.get(id)?.label ?? id}: its split between shop orders and Amazon cannot be trusted, so customer parcels and Amazon inbound are both costed at its combined rate per parcel.</Footnote>)}
  </section>;
}

function CostTables({
  mode,
  actualView = "year",
  ids,
  providers,
  histories,
  rates,
  to,
  fetchTo,
  loading,
  errors,
  gbpRates,
  invoiceMode,
}: {
  mode: "yearly" | "fixed";
  actualView?: "year" | "per_parcel";
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  rates: Map<string, RunRate>;
  to: string;
  fetchTo: string;
  loading: Set<string>;
  errors: Set<string>;
  gbpRates: Map<string, RunRate>;
  invoiceMode: boolean;
}) {
  const rows = ids.map((id) => ({ id, provider: providers.get(id), history: histories.get(id), rate: rates.get(id) }));
  type ActualChartRow = { label: string; yearly?: number | null; total?: number } & Partial<Record<(typeof PARTS)[number], number>>;
  const chartRows = ids
    .map((id) => ({ provider: providers.get(id), rate: gbpRates.get(id) }))
    .filter((row): row is { provider: TplProvider; rate: RunRate } => row.provider != null && row.rate != null && row.rate.yearly != null)
    .reduce<ActualChartRow[]>((result, row) => {
      if (actualView === "year") {
        result.push({ label: row.provider.label, ...row.rate.yearlyParts, yearly: row.rate.yearly });
        return result;
      }
      const values = actualPerParcel(row.rate);
      if (values) result.push({ label: row.provider.label, ...values });
      return result;
    }, [])
    .sort((a, b) => Number(a.yearly ?? a.total) - Number(b.yearly ?? b.total));
  const noParcelRows = actualView === "per_parcel"
    ? rows.filter(({ rate }) => rate != null && rate.monthsBilled > 0 && rate.parcels === 0)
    : [];
  return (
    mode === "yearly" ? <>
      <ChartFrame
        title={`${actualView === "year" ? "Actual yearly cost per 3PL, by part" : "Actual cost per parcel per 3PL, by part"}${invoiceMode ? " (GBP)" : ""}`}
        subtitle={actualView === "year" ? "3PLs billed for fewer months than the range are scaled to a year." : "Each part of the bill divided by the parcels shipped in the same months."}
        titleVisible
        pointCount={chartRows.length}
      >
        <BarChart data={chartRows} layout="vertical" margin={{ left: 12, right: 12 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={(value) => finiteMoney(Number(value), actualView === "year" ? 0 : 2)} />
          <YAxis type="category" dataKey="label" width={96} />
          <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), actualView === "year" ? 0 : 2)} />
          {PARTS.map((part, index) => <Bar key={part} dataKey={part} stackId="a" fill={CRITERIA_COLOURS[index]} />)}
        </BarChart>
      </ChartFrame>
      <Table>
        <TableHeader><TableRow>
          <TableHead>3PL</TableHead><TableHead>Bills up to</TableHead><TableHead>Months billed</TableHead>
          <TableHead className="text-right">Parcels a year</TableHead>
          {PARTS.map((part) => <TableHead key={part} className="text-right">{part}</TableHead>)}
          <TableHead className="text-right">{actualView === "year" ? "Yearly total" : "Per parcel total"}</TableHead>
        </TableRow></TableHeader>
        <TableBody>{rows.map(({ id, provider, history, rate }) => {
          const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
          const perParcelValues = rate ? actualPerParcel(rate) : null;
          return <TableRow key={id}>
            <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
            {empty ? <EmptyProviderCells copy={empty} columns={10} error={errors.has(id)} /> : <>
              <TableCell>{lastBilled(history!, to) ? shortMonth(lastBilled(history!, to)!) : "—"}</TableCell>
              <TableCell>{rate!.monthsBilled}</TableCell>
              <TableCell className="text-right">{Math.round(rate!.parcels * 12 / rate!.monthsBilled).toLocaleString("en-GB")}</TableCell>
              {PARTS.map((part) => <TableCell key={part} className="text-right">{moneyIn(actualView === "year" ? rate!.yearlyParts?.[part] : perParcelValues?.[part], invoiceMode ? provider!.currency : "GBP", actualView === "year" ? 0 : 2)}</TableCell>)}
              <TableCell className="text-right font-semibold">{moneyIn(actualView === "year" ? rate!.yearly : perParcelValues?.total, invoiceMode ? provider!.currency : "GBP", actualView === "year" ? 0 : 2)}</TableCell>
            </>}
          </TableRow>;
        })}</TableBody>
      </Table>
      {noParcelRows.length ? <Note>{noParcelRows.map(({ id, provider }) => provider?.label ?? id).join(", ")}: no parcels in this range, so there is no cost per parcel.</Note> : null}
      {rows.filter(({ history, rate }) => history && rate && rate.monthsBilled > 0 && lastBilled(history, to) && lastBilled(history, to)! < to).map(({ id, provider, history }) => (
        <Note key={id}>{provider!.label}: no bills after {shortMonth(lastBilled(history!, to)!)}; its year is scaled from the months billed.</Note>
      ))}

    </> : <section className="grid gap-3">
        <Table>
          <TableHeader><TableRow>
            <TableHead>3PL</TableHead><TableHead className="text-right">Fixed per year</TableHead><TableHead className="text-right">Variable per year</TableHead>
            <TableHead className="text-right">Other per year</TableHead><TableHead className="text-right">Fixed share</TableHead><TableHead className="text-right">Fixed per month</TableHead>
          </TableRow></TableHeader>
          <TableBody>{rows.map(({ id, provider, history, rate }) => {
            const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
            return <TableRow key={id}>
              <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
              {empty ? <EmptyProviderCells copy={empty} columns={5} error={errors.has(id)} /> : <>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["Subscription", "Storage"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["D2C shipping", "Amazon inbound", "Stock freight"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["Other"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{rate!.fixedShare == null ? "—" : `${Math.round(rate!.fixedShare! * 100)}%`}</TableCell>
                <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
              </>}
            </TableRow>;
          })}</TableBody>
        </Table>
        <Note>Fixed is paid even with no orders. The split is our reading of the cost types and still needs Bojan's confirmation. {invoiceMode ? "TFI's stock freight lines from Feb to May 26 are probably storage" : "TFI's stock freight lines (GBP 1,282, Feb to May 26) are probably storage"}: the v1 page counted them as fixed (TFI 99.7% fixed); here they stay variable, as the engine codes them, until Bojan confirms. Amounts here are per year; the v1 page showed totals for the months billed.</Note>
    </section>
  );
}

function YearTable({ history, label, to, loadedFrom, from, currency, loading, error }: {
  history: TplHistory | undefined;
  label: string;
  to: string;
  loadedFrom: string;
  from: string;
  currency: string;
  loading: boolean;
  error: boolean;
}) {
  const title = `${label} year by year since ${from.slice(0, 4)}`;
  if (loading) return <><SectionTitle>{title}</SectionTitle><Note>Loading…</Note></>;
  if (error) return <><SectionTitle>{title}</SectionTitle><Alert><AlertDescription>{ROW_ERROR}</AlertDescription></Alert></>;
  if (!history) return <><SectionTitle>{title}</SectionTitle><Note>Not in the engine yet</Note></>;
  const rows = yearRows(history, to, loadedFrom, from);
  const suspect = [...new Set(rows.flatMap((row) => row.suspectMonths))].sort();
  const flags = new Map<string, { words: string; months: string[] }>();
  history.months.filter((month) => ym(month.month) >= from && ym(month.month) <= to).forEach((month) => {
    month.flags.forEach((flag) => {
      const current = flags.get(flag.code) ?? { words: flag.words, months: [] };
      current.months.push(ym(month.month));
      flags.set(flag.code, current);
    });
  });
  const valueWithMonths = (value: number | null, count: number, billed: number) => `${moneyIn(value, currency, 0)}${count < billed ? ` (${count} months)` : ""}`;
  return (
    <section className="grid gap-3">
      <SectionTitle>{title}</SectionTitle>
      <Table>
        <TableHeader><TableRow>
          {[
            "Year", "Months billed", "Parcels", "Cost", "Cost per parcel", "Change", "Pick and Pack per non-Amazon parcel", "Change",
            "Packaging per non-Amazon parcel", "Change", "Courier per non-Amazon parcel", "Change", "Storage per month", "Change", "Subscription per month",
          ].map((heading, index) => <TableHead key={`${heading}-${index}`} className={index > 0 ? "text-right" : undefined}>{heading}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>{rows.map((row) => {
          const label = `${row.label}${row.loaded === "partial" ? " (part loaded)" : ""}`;
          if (row.loaded === "none" || row.monthsBilled === 0) return (
            <TableRow key={row.year}><TableCell>{label}</TableCell><TableCell colSpan={14} className="text-muted-foreground">{row.loaded === "none" ? "Not loaded" : "No bills"}</TableCell></TableRow>
          );
          return <TableRow key={row.year}>
            <TableCell>{label}</TableCell><TableCell className="text-right">{row.monthsBilled}</TableCell><TableCell className="text-right">{row.parcels.toLocaleString("en-GB")}</TableCell>
            <TableCell className="text-right">{moneyIn(row.cost, currency, 0)}</TableCell><TableCell className="text-right">{moneyIn(row.costPerParcel, currency, 2)}</TableCell><TableCell className="text-right">{percent(row.costPerParcelChange, row.changeSuspect)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.perNonFba.pick_pack, currency, 2)}</TableCell><TableCell className="text-right">{percent(row.perNonFbaChange.pick_pack, row.changeSuspect)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.perNonFba.packaging, currency, 2)}</TableCell><TableCell className="text-right">{percent(row.perNonFbaChange.packaging, row.changeSuspect)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.perNonFba.courier, currency, 2)}</TableCell><TableCell className="text-right">{percent(row.perNonFbaChange.courier, row.changeSuspect)}</TableCell>
            <TableCell className="text-right">{valueWithMonths(row.storagePerMonth, row.storageMonths, row.monthsBilled)}</TableCell><TableCell className="text-right">{percent(row.storagePerMonthChange, row.changeSuspect)}</TableCell>
            <TableCell className="text-right">{valueWithMonths(row.subscriptionPerMonth, row.subscriptionMonths, row.monthsBilled)}</TableCell>
          </TableRow>;
        })}</TableBody>
      </Table>
      {rows.some((row) => row.changeSuspect) ? <Footnote>* The year or the year before includes a month whose bill detail does not tie ({suspect.map(shortMonth).join(", ")}), so the change may reflect billing rather than a price rise.</Footnote> : null}
      {[...flags.entries()].map(([code, flag]) => <Footnote key={code}>{flag.words} ({[...new Set(flag.months)].sort().map(shortMonth).join(", ")})</Footnote>)}
    </section>
  );
}

const PER_PARCEL_ROWS: Array<{ key: PerParcelKey; label: string }> = [
  { key: "pick_pack", label: "Pick and Pack" }, { key: "packaging", label: "Packaging" }, { key: "courier", label: "Courier" },
  { key: "d2c", label: "All D2C shipping (per non-Amazon parcel)" }, { key: "d2c_amazon", label: "D2C plus Amazon inbound (per parcel, all parcels)" },
];
const perParcelLabel = (value: PerParcel, currency: string): string => value === "not_itemised" ? "Not itemised" : value === "unreliable" ? "Not reliable (Amazon split)" : moneyIn(value, currency, 2);

function ComparisonTable({ ids, providers, histories, gbpHistories, loading, errors, from, to, defaultComparison, invoiceMode }: {
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  gbpHistories: Map<string, TplHistory>;
  loading: Set<string>;
  errors: Set<string>;
  from: string;
  to: string;
  defaultComparison: boolean;
  invoiceMode: boolean;
}) {
  const columns = ids.map((id) => ({ id, provider: providers.get(id), history: histories.get(id) }));
  const labels = columns.map(({ id, provider }) => provider?.label ?? id);
  const values = columns.map(({ history }) => history ? perParcelByPart(history, from, to) : null);
  const gbpValues = ids.map((id) => gbpHistories.get(id)).map((history) => history ? perParcelByPart(history, from, to) : null);
  const showDifference = columns.length === 2;
  const sameInvoiceCurrency = !invoiceMode || (columns[0]?.provider?.currency != null && columns[0].provider.currency === columns[1]?.provider?.currency);
  const title = showDifference ? `Cost per parcel by part: ${labels[0]} vs ${labels[1]}` : "Cost per parcel by part";
  return <section className="grid gap-3">
    <SectionTitle>{title}</SectionTitle>
    <Table><TableHeader><TableRow><TableHead>Part</TableHead>
      {labels.map((label, index) => <TableHead key={columns[index].id} className="text-right">{label}</TableHead>)}
      {showDifference ? <TableHead className="text-right">{`${defaultComparison ? "Difference" : `${labels[1]} minus ${labels[0]}`}${invoiceMode ? " (GBP)" : ""}`}</TableHead> : null}
    </TableRow></TableHeader>
      <TableBody>{PER_PARCEL_ROWS.map(({ key, label }) => {
        const gbpA = gbpValues[0]?.[key] ?? null; const gbpB = gbpValues[1]?.[key] ?? null;
        const difference = sameInvoiceCurrency && typeof gbpA === "number" && typeof gbpB === "number" ? gbpB - gbpA : null;
        return <TableRow key={key}><TableCell>{label}</TableCell>
          {columns.map(({ id, provider, history }, index) => <TableCell key={id} className="text-right">
            {!provider ? "Not in the engine yet" : loading.has(id) ? "Loading…" : errors.has(id) ? <span role="alert">{ROW_ERROR}</span> : history ? perParcelLabel(values[index]?.[key] ?? null, invoiceMode ? provider.currency : "GBP") : "No bills in this range"}
          </TableCell>)}
          {showDifference ? <TableCell className="text-right">{finiteMoney(difference)}</TableCell> : null}
        </TableRow>;
      })}</TableBody>
    </Table>
    <Note>Per parcel over the months with parcels. Where the 3PL's split between shop orders and Amazon cannot be trusted (a negative part, or the engine's Amazon-gap flag on half the months or more), only the last row compares like for like.</Note>
  </section>;
}

function WhatIf({
  histories, rates, from, to, receivers, receiving, showMove, showAged, extraRaw, agedRaw, setSearch, invoiceMode,
}: {
  histories: Map<string, TplHistory>; rates: Map<string, RunRate>; from: string; to: string;
  receivers: ProviderId[]; receiving: ProviderId; showMove: boolean; showAged: boolean;
  extraRaw: string; agedRaw: string; setSearch: (key: string, value: string) => void;
  invoiceMode: boolean;
}) {
  const receivingHistory = histories.get(receiving);
  const receivingD2c = receivingHistory ? perParcelByPart(receivingHistory, from, to).d2c : null;
  const tfi = rates.get("tfi"); const jjUk = rates.get("jj_uk");
  const extra = inputNumber(extraRaw); const aged = inputNumber(agedRaw);
  const move = tfi ? moveTfiSaving(tfi, receivingD2c, extra) : null;
  const clear = jjUk ? agedStockSaving(jjUk, aged) : null;
  return <section className="grid gap-3">
    <SectionTitle>{`What if${invoiceMode ? " (GBP)" : ""}`}</SectionTitle>
    <div className="grid gap-4 lg:grid-cols-2">
      {showMove ? <Card><CardHeader><CardTitle>Move TFI orders</CardTitle><CardDescription>TFI's non-Amazon orders are costed at the receiving 3PL's D2C cost per parcel; its Amazon parcels are not costed.</CardDescription></CardHeader><CardContent className="grid gap-4">
        <label className="grid gap-1 text-sm">Move TFI orders to
          <Select value={receiving} onValueChange={(value) => setSearch("tc_rcv", value)}><SelectTrigger data-nonmutating><SelectValue /></SelectTrigger><SelectContent>{receivers.map((id) => <SelectItem key={id} value={id}>{histories.get(id)?.provider.label ?? id}</SelectItem>)}</SelectContent></Select>
        </label>
        <label className="grid gap-1 text-sm">Extra freight to the UAE per year (GBP)<Input data-nonmutating type="number" min={0} value={extraRaw} onChange={(event) => setSearch("tc_extra", event.target.value)} /></label>
        <p className="font-medium">{!tfi || !receivingHistory ? "Not enough data to work this out." : extraRaw === "" ? "Type the extra freight to the UAE to see the saving." : move == null ? "Not enough data to work this out." : <>Saving per year, before delivery inside the UAE (upper bound): {finiteMoney(move, 0)}</>}</p>
      </CardContent></Card> : <Note>Choose TFI and J&amp;J UK or Omnipack in the 3PLs filter to use this what-if.</Note>}
      {showAged ? <Card><CardHeader><CardTitle>Clear J&amp;J UK aged stock</CardTitle><CardDescription>Take the aged share from J&amp;J's latest storage report.</CardDescription></CardHeader><CardContent className="grid gap-4">
        <label className="grid gap-1 text-sm">Aged stock share of J&amp;J UK storage (%)<Input data-nonmutating type="number" min={0} max={100} value={agedRaw} onChange={(event) => setSearch("tc_aged", event.target.value)} /></label>
        <p className="font-medium">{clear == null ? "Type the aged share to see the saving." : <>Saving per year: {finiteMoney(clear, 0)}</>}</p>
      </CardContent></Card> : <Note>Choose J&amp;J UK in the 3PLs filter to use this what-if.</Note>}
    </div>
    {showMove && showAged && move != null && clear != null ? <p className="text-lg font-semibold">Both together: {finiteMoney(move + clear, 0)}</p> : null}
  </section>;
}

export function TplCostModelPage(_props: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const fetchTo = lastCompleteMonth(new Date());
  const loadedFrom = fetchFrom(fetchTo);
  const providersQuery = useQuery({ queryKey: [...QUERY_ROOT, "providers"], queryFn: getTplProviders, staleTime: 60_000 });
  const providers = providersQuery.data?.data.providers ?? [];
  const providersById = providerMap(providers);
  const allCompanyOptions = companyOptions(providers, []);
  const companies = parseList(search.tc_co).filter((company) => allCompanyOptions.includes(company));
  const availableRegions = regionOptions(providers, companies);
  const regions = parseList(search.tc_rg).filter((region) => availableRegions.includes(region));
  const availableCompanies = companyOptions(providers, regions);
  const options = selectableProviders(providers, companies, regions);
  const picked = parseList(search.tc_pv);
  const filtered = companies.length > 0 || regions.length > 0;
  const chosen = chosenIds(options, picked, filtered);
  const invoiceMode = search.tc_cur === "invoice";
  const currentServed = CURRENT_3PLS.filter((id) => providersById.has(id));
  const fetchIds = [...new Set([...currentServed, ...chosen.filter((id) => providersById.has(id))])];
  const historyQueries = useQueries({ queries: fetchIds.map((id) => ({
    queryKey: [...QUERY_ROOT, "history", id, loadedFrom, fetchTo],
    queryFn: () => getTplHistory({ provider: id, from: loadedFrom, to: fetchTo, currency: "gbp" }),
    staleTime: 60_000,
  })) });
  const invoiceHistoryQueries = useQueries({ queries: fetchIds.map((id) => ({
    queryKey: [...QUERY_ROOT, "history", id, loadedFrom, fetchTo, "invoice"],
    queryFn: () => getTplHistory({ provider: id, from: loadedFrom, to: fetchTo, currency: "invoice" }),
    enabled: invoiceMode,
    staleTime: 60_000,
  })) });
  const loadedGbpHistories = historyQueries.flatMap((query) => query.data?.data ? [query.data.data] : []);
  const loadedInvoiceHistories = invoiceHistoryQueries.flatMap((query) => query.data?.data ? [query.data.data] : []);
  const gbpHistories = new Map(loadedGbpHistories.map((history) => [history.provider.provider_id, history]));
  const invoiceHistories = new Map(loadedInvoiceHistories.map((history) => [history.provider.provider_id, history]));
  const histories = invoiceMode ? invoiceHistories : gbpHistories;
  const baselineHistories = currentServed.flatMap((id) => gbpHistories.get(id) ? [gbpHistories.get(id)!] : []);
  const effectiveDefault = defaultTo(baselineHistories, fetchTo);
  const to = validTo(search.tc_to, fetchTo) ?? effectiveDefault;
  const from = validFrom(search.tc_from, to, fetchTo) ?? defaultFrom(to, fetchTo);
  const gbpRates = new Map(loadedGbpHistories.map((history) => [history.provider.provider_id, runRate(history, from, to)]));
  const invoiceRates = new Map(loadedInvoiceHistories.map((history) => [history.provider.provider_id, runRate(history, from, to)]));
  const rates = invoiceMode ? invoiceRates : gbpRates;
  const setSearch = (key: string, value: string) => void navigate({ search: ((previous: Record<string, unknown>) => {
    const next = { ...(previous as Record<string, unknown>) };
    if (value === "") delete next[key]; else next[key] = value;
    return next;
  }) as never } as never);
  const setLinkedFilter = (key: "tc_co" | "tc_rg", values: string[]) => void navigate({ search: ((previous: Record<string, unknown>) => {
    const next = { ...(previous as Record<string, unknown>) };
    const nextCompanies = (key === "tc_co" ? values : companies).filter((company) => allCompanyOptions.includes(company));
    const nextRegionOptions = regionOptions(providers, nextCompanies);
    const nextRegions = (key === "tc_rg" ? values : parseList(previous.tc_rg)).filter((region) => nextRegionOptions.includes(region));
    if (nextCompanies.length) next.tc_co = nextCompanies.join(","); else delete next.tc_co;
    if (nextRegions.length) next.tc_rg = nextRegions.join(","); else delete next.tc_rg;
    return next;
  }) as never } as never);
  const queryById = new Map(fetchIds.map((id, index) => [id, historyQueries[index]]));
  const invoiceQueryById = new Map(fetchIds.map((id, index) => [id, invoiceHistoryQueries[index]]));
  const displayQueryById = invoiceMode ? invoiceQueryById : queryById;
  const baselineErrors = currentServed.map((id) => queryById.get(id)?.error).filter(Boolean);
  const failure = providersQuery.error ?? baselineErrors[0];
  const status = [providersQuery.error, ...baselineErrors].some((error) => errorStatus(error) === 403)
    ? 403 : errorStatus(providersQuery.error) === 404 ? 404 : null;
  const pending = providersQuery.isPending || currentServed.some((id) => queryById.get(id)?.isPending);
  const loading = new Set(chosen.filter((id) => displayQueryById.get(id)?.isPending));
  const errors = new Set(chosen.filter((id) => displayQueryById.get(id)?.isError));
  const gbpLoading = new Set(chosen.filter((id) => queryById.get(id)?.isPending));
  const gbpErrors = new Set(chosen.filter((id) => queryById.get(id)?.isError));
  const view = search.tc_view === "per_parcel" ? "per_parcel" : "total";
  const actualView = search.tc_actual === "per_parcel" ? "per_parcel" : "year";
  const metric: IndexMetric = INDEX_METRICS.some(({ value }) => value === search.tc_metric) ? search.tc_metric as IndexMetric : "per_parcel";
  const receivers = (["jj_uk", "omnipack"] as ProviderId[]).filter((id) => chosen.includes(id));
  const receiving: ProviderId = typeof search.tc_rcv === "string" && receivers.includes(search.tc_rcv as ProviderId)
    ? search.tc_rcv as ProviderId
    : receivers[0] ?? "jj_uk";
  const showMove = chosen.includes("tfi") && receivers.length > 0;
  const showAged = chosen.includes("jj_uk");
  const yearIds = chosen.filter((id) => providersById.has(id));
  const yearId = typeof search.tc_yr === "string" && yearIds.includes(search.tc_yr) ? search.tc_yr : yearIds[0];
  const extraRaw = typeof search.tc_extra === "string" ? search.tc_extra : "";
  const agedRaw = typeof search.tc_aged === "string" ? search.tc_aged : "";
  const driverDefaults = defaultDrivers(gbpRates.get("jj_uk"));
  const d2cRaw = typeof search.tc_d2c === "string" ? search.tc_d2c : driverDefaults?.d2c.toString() ?? "";
  const fbaRaw = typeof search.tc_fba === "string" ? search.tc_fba : driverDefaults?.fba.toString() ?? "";
  const parsedD2c = inputNumber(d2cRaw);
  const parsedFba = inputNumber(fbaRaw);
  const driverCandidate = parsedD2c == null || parsedFba == null ? null : { d2c: parsedD2c, fba: parsedFba };
  const drivers = validDrivers(driverCandidate) ? driverCandidate : null;
  const driversTyped = typeof search.tc_d2c === "string" || typeof search.tc_fba === "string";
  const standardisedRows = drivers ? rankStandardised(chosen.flatMap((id) => {
    const rate = gbpRates.get(id);
    const history = gbpHistories.get(id);
    if (!rate || !history) return [];
    const splitUnreliable = perParcelByPart(history, from, to).d2c === "unreliable";
    return [standardised(rate, drivers, splitUnreliable)];
  })) : [];
  const selectedHistories = chosen.flatMap((id) => histories.get(id) ? [histories.get(id)!] : []);
  const selectedGbpHistories = chosen.flatMap((id) => gbpHistories.get(id) ? [gbpHistories.get(id)!] : []);
  const trends = trendRows(selectedHistories, from, to);
  const gbpTrends = trendRows(selectedGbpHistories, from, to);
  const belowFloorIds = belowIndexFloor(selectedGbpHistories, from, to);
  const indices = indexRows(selectedGbpHistories, from, to, metric);
  const indexGapIds = indexGaps(selectedGbpHistories, from, to, metric);
  const metricLabel = INDEX_METRICS.find(({ value }) => value === metric)!.label;
  const suspectRates = chosen.flatMap((id) => {
    const rate = rates.get(id); const provider = providersById.get(id);
    return rate && provider && rate.splitSuspectMonths.length ? [{ rate, provider }] : [];
  });
  const defaultComparison = !filtered && !options.some((provider) => picked.includes(provider.provider_id));
  const comparisonIds = defaultComparison ? ["jj_uk", "omnipack"] : chosen;

  return <div className="grid gap-8">
    <PageHead title="3PL Cost Model" sub={invoiceMode ? "What our chosen 3PLs cost at the same volume (standardised) and as billed (actual), from the bills in Precision Fulfilment's 3PL History (each 3PL's invoice currency; totals in GBP), for the months you pick. Yearly amounts are scaled from the months billed." : "What our chosen 3PLs cost at the same volume (standardised) and as billed (actual), from the bills in Precision Fulfilment's 3PL History (GBP), for the months you pick. Yearly amounts are scaled from the months billed."} kpi={null} />
    {failure ? <Failure status={status} /> : pending ? <Pending /> : <>
      <div className="flex flex-wrap items-end gap-4">
        <MultiPick label="Company" options={availableCompanies.map((value) => ({ value, label: COMPANY_LABEL[value] }))} value={companies.filter((company) => availableCompanies.includes(company))} onChange={(next) => setLinkedFilter("tc_co", next)} />
        <MultiPick label="Region" options={availableRegions.map((value) => ({ value, label: value }))} value={regions} onChange={(next) => setLinkedFilter("tc_rg", next)} />
        <MultiPick label="3PLs" options={options.map((provider) => ({ value: provider.provider_id, label: provider.label }))} value={options.map((provider) => provider.provider_id).filter((id) => picked.includes(id))} onChange={(next) => setSearch("tc_pv", next.join(","))} />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="grid gap-1 text-sm">From<Input data-nonmutating type="month" min={loadedFrom} max={to} value={from} onChange={(event) => setSearch("tc_from", event.target.value)} /></label>
        <label className="grid gap-1 text-sm">To<Input data-nonmutating type="month" min={from} max={fetchTo} value={to} onChange={(event) => setSearch("tc_to", event.target.value)} /></label>
        <FilterBar layout="segmented" filters={[{ name: "tc_cur", label: "Currency", kind: "choice", options: [{ value: "invoice", label: "Invoice" }, { value: "gbp", label: "GBP" }] }]} values={{ tc_cur: invoiceMode ? "invoice" : "gbp" }} onChange={(next) => setSearch("tc_cur", next.tc_cur === "invoice" ? "invoice" : "")} />
        <p className="pb-2 text-sm text-muted-foreground">Bills loaded to {shortMonth(effectiveDefault)}</p>
      </div>
      {invoiceMode ? <Note>Per-3PL amounts are in each 3PL's invoice currency, as billed. Totals and comparisons across 3PLs stay in GBP.</Note> : null}
      {chosen.length === 0 ? <Note>No 3PL matches these filters.</Note> : <>
        <StandardisedCostTable ids={chosen} providers={providersById} histories={gbpHistories} rates={gbpRates} loading={gbpLoading} errors={gbpErrors} fetchTo={fetchTo} from={from} to={to} drivers={drivers} driversTyped={driversTyped} d2cRaw={d2cRaw} fbaRaw={fbaRaw} rows={standardisedRows} setSearch={setSearch} invoiceMode={invoiceMode} />
        <section className="grid gap-3">
          <FilterBar layout="segmented" filters={[{ name: "tc_metric", label: "Metric", kind: "choice", options: [...INDEX_METRICS] }]} values={{ tc_metric: metric }} onChange={(next) => setSearch("tc_metric", next.tc_metric === "per_parcel" ? "" : String(next.tc_metric ?? ""))} />
          <ChartFrame title={`Cost index per 3PL, ${metricLabel} (first billed month = 100)`} titleVisible pointCount={selectedGbpHistories.length ? indices.length : 0}>
            <LineChart data={indices} margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={shortMonth} />
              <YAxis width={48} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value, _name, item) => {
                const payload = item.payload as Record<string, number | string | null>;
                const raw = payload[`${String(item.dataKey)}_v`];
                return `${Number(value)} (${finiteMoney(typeof raw === "number" ? raw : null, 2)})`;
              }} />
              <Legend />
              {chosen.filter((id) => gbpHistories.has(id)).map((id) => {
                const provider = providersById.get(id);
                return <Line key={id} type="monotone" dataKey={id} name={`${provider?.label ?? id} (${provider?.region ?? "unknown region"})`} stroke={lineColour(id, chosen)} strokeDasharray={belowFloorIds.includes(id) ? "4 4" : undefined} connectNulls={false} dot={false} />;
              })}
            </LineChart>
          </ChartFrame>
          <Note>100 is each 3PL's first billed month in the range, so the lines show change, not level.</Note>
          {belowFloorIds.length ? <Note>{belowFloorIds.map((id) => providersById.get(id)?.label ?? id).join(", ")}: under {INDEX_FLOOR} parcels a month, so their lines move with a few orders, not price.</Note> : null}
          {indexGapIds.length ? <Note>{indexGapIds.map((id) => providersById.get(id)?.label ?? id).join(", ")}: no {metricLabel.toLowerCase()} in this range.</Note> : null}
        </section>
        <section className="grid gap-3">
          <SectionTitle>Actuals (what each 3PL billed)</SectionTitle>
          <Accordion type="multiple" defaultValue={["yearly", "fixed", "years", "parts", "what-if", "monthly"]}>
            <AccordionItem value="yearly"><AccordionTrigger data-nonmutating>Actual yearly cost per 3PL, by part</AccordionTrigger><AccordionContent className="grid gap-3">
              <FilterBar layout="segmented" filters={[{ name: "tc_actual", label: "Values", kind: "choice", options: [{ value: "year", label: "Year" }, { value: "per_parcel", label: "Per parcel" }] }]} values={{ tc_actual: actualView }} onChange={(next) => setSearch("tc_actual", next.tc_actual === "per_parcel" ? "per_parcel" : "")} />
              <CostTables mode="yearly" actualView={actualView} ids={chosen} providers={providersById} histories={histories} rates={rates} to={to} fetchTo={fetchTo} loading={loading} errors={errors} gbpRates={gbpRates} invoiceMode={invoiceMode} />
            </AccordionContent></AccordionItem>
            <AccordionItem value="fixed"><AccordionTrigger data-nonmutating>Actual fixed vs variable</AccordionTrigger><AccordionContent><CostTables mode="fixed" ids={chosen} providers={providersById} histories={histories} rates={rates} to={to} fetchTo={fetchTo} loading={loading} errors={errors} gbpRates={gbpRates} invoiceMode={invoiceMode} /></AccordionContent></AccordionItem>
            <AccordionItem value="years"><AccordionTrigger data-nonmutating>{yearId ? `${providersById.get(yearId)?.label ?? yearId} year by year since ${from.slice(0, 4)}` : "3PL year by year"}</AccordionTrigger><AccordionContent className="grid gap-3">
              {yearId ? <><label className="grid w-fit gap-1 text-sm">3PL<Select value={yearId} onValueChange={(value) => setSearch("tc_yr", value)}><SelectTrigger data-nonmutating><SelectValue /></SelectTrigger><SelectContent>{yearIds.map((id) => <SelectItem key={id} value={id}>{providersById.get(id)?.label ?? id}</SelectItem>)}</SelectContent></Select></label><YearTable history={histories.get(yearId)} label={providersById.get(yearId)?.label ?? yearId} to={to} loadedFrom={loadedFrom} from={from} currency={invoiceMode ? providersById.get(yearId)?.currency ?? "GBP" : "GBP"} loading={displayQueryById.get(yearId)?.isPending === true} error={displayQueryById.get(yearId)?.isError === true} /></> : <Note>Choose a served 3PL to see its year-by-year costs.</Note>}
            </AccordionContent></AccordionItem>
            <AccordionItem value="parts"><AccordionTrigger data-nonmutating>Cost per parcel by part</AccordionTrigger><AccordionContent><ComparisonTable ids={comparisonIds} providers={providersById} histories={histories} gbpHistories={gbpHistories} loading={loading} errors={errors} from={from} to={to} defaultComparison={defaultComparison} invoiceMode={invoiceMode} /></AccordionContent></AccordionItem>
            <AccordionItem value="what-if"><AccordionTrigger data-nonmutating>What if</AccordionTrigger><AccordionContent><WhatIf histories={gbpHistories} rates={gbpRates} from={from} to={to} receivers={receivers} receiving={receiving} showMove={showMove} showAged={showAged} extraRaw={extraRaw} agedRaw={agedRaw} setSearch={setSearch} invoiceMode={invoiceMode} /></AccordionContent></AccordionItem>
            <AccordionItem value="monthly"><AccordionTrigger data-nonmutating>Actual monthly cost per 3PL</AccordionTrigger><AccordionContent className="grid gap-3">
              <FilterBar layout="segmented" filters={[{ name: "tc_view", label: "Values", kind: "choice", options: [{ value: "total", label: "Total" }, { value: "per_parcel", label: "Per parcel" }] }]} values={{ tc_view: view }} onChange={(next) => setSearch("tc_view", next.tc_view === "per_parcel" ? "per_parcel" : "")} />
              {view === "total" ? <ChartFrame title={`Actual monthly cost per 3PL${invoiceMode ? " (GBP)" : ""}`} titleVisible pointCount={selectedGbpHistories.length ? gbpTrends.length : 0}><LineChart data={gbpTrends} margin={{ left: 8, right: 8 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tickFormatter={shortMonth} /><YAxis width={72} tickFormatter={(value) => finiteMoney(Number(value), 0)} /><Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), 0)} /><Legend />{chosen.filter((id) => gbpHistories.has(id)).map((id) => <Line key={id} type="monotone" dataKey={id} name={providersById.get(id)?.label ?? id} stroke={lineColour(id, chosen)} connectNulls={false} dot={false} />)}</LineChart></ChartFrame> : <><div className="grid gap-4 md:grid-cols-2">{chosen.filter((id) => histories.has(id)).map((id) => { const label = providersById.get(id)?.label ?? id; return <ChartFrame key={id} title={label} titleVisible pointCount={trends.length}><LineChart data={trends} margin={{ left: 8, right: 8 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tickFormatter={shortMonth} /><YAxis width={72} tickFormatter={(value) => moneyIn(Number(value), invoiceMode ? providersById.get(id)?.currency ?? "GBP" : "GBP", 2)} /><Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => moneyIn(Number(value), invoiceMode ? providersById.get(id)?.currency ?? "GBP" : "GBP", 2)} /><Line type="monotone" dataKey={`${id}_pp`} name={label} stroke={lineColour(id, chosen)} connectNulls={false} dot={{ r: 2 }} /></LineChart></ChartFrame>; })}</div><Note>Each 3PL has its own scale. A gap is a month with no bill or no parcels.</Note></>}
            </AccordionContent></AccordionItem>
          </Accordion>
        </section>
      </>}
      {suspectRates.length ? <Alert><AlertDescription>
        <p>In these months the bill's detail does not tie to its total, so the split by part above may be off; the month total is still the bill (DEC-1231).</p>
        {suspectRates.map(({ provider, rate }) => <p key={provider.provider_id}>{provider.label}: {rate.splitSuspectMonths.length} month{rate.splitSuspectMonths.length === 1 ? "" : "s"}, {rate.splitSuspectMonths.length === 1 ? shortMonth(rate.splitSuspectMonths[0]) : `${shortMonth(rate.splitSuspectMonths[0])} to ${shortMonth(rate.splitSuspectMonths.at(-1)!)}`}</p>)}
      </AlertDescription></Alert> : null}
    </>}
  </div>;
}
